import React, { ReactElement, useContext } from 'react'

import { LanguageContext } from '../../Common/Contexts'
import LanguageHandler from '../../../NonReact/Languages/LanguageHandler'

import Heading from '../../Common/Components/Heading'
import FormRow from '../../Common/Components/FormRow'
import Fieldset from '../../Common/Components/Fieldset'
import TextInput from '../../Common/Components/TextInput'
import { StatisticsConfiguration } from './Types'
import { STATISTICS_CUSTOM_WIDTH_DEFAULT, STATISTICS_CUSTOM_WIDTH_TEXT_DEFAULT } from './Constants'
import { CustomSettings as CustomSettingsLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'

type CustomSettingsProps = {
    selectedStatisticsConfiguration: StatisticsConfiguration | undefined
    updateSelectedStatisticsConfiguration: (updatedStatisticsConfiguration: StatisticsConfiguration) => void
    isWallboardReport: boolean
}

const CustomSettings: React.FC<CustomSettingsProps> = ({
    selectedStatisticsConfiguration,
    updateSelectedStatisticsConfiguration,
    isWallboardReport
}): ReactElement => {
    const language = useContext(LanguageContext) as LanguageHandler
    language.loadBundle('CustomSettings')

    const isStatisticsSelected = selectedStatisticsConfiguration !== undefined
    const customName = selectedStatisticsConfiguration ? selectedStatisticsConfiguration.customName : ''
    const customWidth =
        selectedStatisticsConfiguration &&
        selectedStatisticsConfiguration.customWidth !== STATISTICS_CUSTOM_WIDTH_DEFAULT
            ? selectedStatisticsConfiguration.customWidth
            : ''

    const setCustomName = (value: string): void => {
        if (selectedStatisticsConfiguration) {
            updateSelectedStatisticsConfiguration({
                ...selectedStatisticsConfiguration,
                customName: value
            })
        }
    }

    const setCustomWidth = (value: string): void => {
        let newWidthValue = parseInt(value, 10)
        if (Number.isNaN(newWidthValue)) {
            newWidthValue = STATISTICS_CUSTOM_WIDTH_DEFAULT
        }

        if (selectedStatisticsConfiguration) {
            updateSelectedStatisticsConfiguration({
                ...selectedStatisticsConfiguration,
                customWidth: newWidthValue
            })
        }
    }

    return (
        <Fieldset className='display-settings'>
            <Heading level={HeadingLevel.TWO}>
                {language?.get(CustomSettingsLanguage.CustomSettings_SectionHeader)}
                <HelpTag
                    className='configure-report-help-title-tag'
                    helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_FIELDS_CUSTOM_SETTINGS}
                    tag={Tags.CONFIGURE_REPORT}
                />
            </Heading>

            <FormRow>
                <TextInput
                    value={customName}
                    onChange={setCustomName}
                    labelLeft={language?.get(CustomSettingsLanguage.CustomNameLabel)}
                    disabled={!isStatisticsSelected}
                    placeholder={
                        isWallboardReport
                            ? selectedStatisticsConfiguration?.mediumName
                            : selectedStatisticsConfiguration?.shortName
                    }
                />
            </FormRow>
            <FormRow>
                {!isWallboardReport && (
                    <TextInput
                        value={customWidth}
                        placeholder={STATISTICS_CUSTOM_WIDTH_TEXT_DEFAULT}
                        onChange={setCustomWidth}
                        labelLeft={language?.get(CustomSettingsLanguage.CustomWidthLabel)}
                        disabled={!isStatisticsSelected}
                    />
                )}
            </FormRow>
        </Fieldset>
    )
}

export default CustomSettings
